import Swal from 'sweetalert2';

export function openExternalUrl(url: string): Window | null {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
  return newWindow;
}

export function openBlankWindow(): Window | null {
  const newWindow = window.open('', '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
  return newWindow;
}

export function openPrintWindow(features?: string): Window | null {
  const newWindow = window.open('about:blank', '_blank', features);

  if (newWindow) {
    newWindow.opener = null;
  }

  return newWindow;
}

export async function downloadPrescriptionPdf(
  fullHtmlString: string,
  fileName: string = 'receta.pdf',
  loaderOptions?: {
    title?: string;
    subtitle?: string;
    marginCm?: number;
  }
): Promise<void> {
  showPrescriptionDownloadLoader(loaderOptions);

  try {
    const pdfOutput = await buildPrescriptionPdfOutput(fullHtmlString, loaderOptions?.marginCm ?? 2);
    pdfOutput.pdf.save(normalizePdfFileName(fileName));
  } finally {
    Swal.close();
  }
}

export async function generatePrescriptionPdfBase64(
  fullHtmlString: string,
  loaderOptions?: {
    title?: string;
    subtitle?: string;
    marginCm?: number;
  }
): Promise<string> {
  showPrescriptionDownloadLoader(loaderOptions);

  try {
    const pdfOutput = await buildPrescriptionPdfOutput(fullHtmlString, loaderOptions?.marginCm ?? 2);
    return pdfOutput.pdf.output('datauristring');
  } finally {
    Swal.close();
  }
}

async function buildPrescriptionPdfOutput(
  fullHtmlString: string,
  marginCm: number
): Promise<{ pdf: any }> {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf')
  ]);

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-10000px';
  iframe.style.left = '-10000px';
  iframe.style.width = '1400px';
  iframe.style.height = '1px';
  iframe.style.opacity = '0';
  iframe.setAttribute('aria-hidden', 'true');

  document.body.appendChild(iframe);

  try {
    await writeHtmlToIframe(iframe, fullHtmlString);

    const iframeWindow = iframe.contentWindow;
    const iframeDocument = iframe.contentDocument;

    if (!iframeWindow || !iframeDocument?.body) {
      throw new Error('No se pudo preparar la receta para PDF.');
    }

    await waitForAssetsToRender(iframeWindow);

    const target = findBestPrescriptionRoot(iframeDocument);
    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      windowWidth: Math.max(
        target.scrollWidth,
        iframeDocument.documentElement?.scrollWidth ?? 0,
        1400
      ),
      width: Math.max(target.scrollWidth, target.clientWidth, 1),
      height: Math.max(target.scrollHeight, target.clientHeight, 1)
    });

    const croppedCanvas = cropCanvasWhitespace(canvas, 4);
    const marginPx = cmToPx(marginCm);
    const contentWidth = Math.max(croppedCanvas.width, 1);
    const contentHeight = Math.max(croppedCanvas.height, 1);
    const pageWidth = contentWidth + (marginPx * 2);
    const pageHeight = contentHeight + (marginPx * 2);
    const orientation = pageWidth >= pageHeight ? 'landscape' : 'portrait';
    const pdf = new jsPDF({
      orientation,
      unit: 'px',
      format: [pageWidth, pageHeight]
    });

    pdf.addImage(
      croppedCanvas.toDataURL('image/png'),
      'PNG',
      marginPx,
      marginPx,
      contentWidth,
      contentHeight,
      undefined,
      'FAST'
    );

    return { pdf };
  } finally {
    iframe.remove();
  }
}

function showPrescriptionDownloadLoader(loaderOptions?: {
  title?: string;
  subtitle?: string;
}): void {
  const title = loaderOptions?.title?.trim() || 'Descargando receta...';
  const subtitle = loaderOptions?.subtitle?.trim() || 'Estamos generando tu PDF';

  Swal.fire({
    html: `
      <div style="display:flex;justify-content:center;">
        <div style="
          width:min(92vw, 420px);
          background:#ffffff;
          border-radius:26px;
          padding:34px 28px 30px;
          box-shadow:0 22px 48px rgba(0,0,0,.16);
          text-align:center;
        ">
          <div style="
            width:56px;
            height:56px;
            margin:0 auto 18px;
            border:4px solid rgba(7,59,96,.12);
            border-top-color:#22b8cf;
            border-right-color:#073b60;
            border-radius:50%;
            animation:rmd-spin 1s linear infinite;
          "></div>
          <div style="
            font-size:1.18rem;
            font-weight:800;
            color:#073b60;
            letter-spacing:.8px;
            text-transform:uppercase;
          ">
            ${title}
          </div>
          <div style="
            width:72%;
            height:1px;
            margin:14px auto 12px;
            background:rgba(7,59,96,.12);
          "></div>
          <div style="
            font-size:.92rem;
            color:#4a5565;
            font-weight:600;
            letter-spacing:.5px;
            text-transform:uppercase;
          ">
            ${subtitle}
          </div>
        </div>
      </div>
      <style>
        @keyframes rmd-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      </style>
    `,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    backdrop: 'rgba(255,255,255,0.92)',
    background: 'transparent',
    width: 'auto',
    padding: 0,
    customClass: {
      popup: 'rmd-download-popup',
      htmlContainer: 'rmd-download-popup-html'
    },
    didOpen: () => {
      Swal.showLoading();
      const loader = document.querySelector('.swal2-loader') as HTMLElement | null;
      if (loader) {
        loader.style.display = 'none';
      }
    }
  });
}

function writeHtmlToIframe(iframe: HTMLIFrameElement, fullHtmlString: string): Promise<void> {
  return new Promise((resolve, reject) => {
    iframe.onload = () => resolve();
    iframe.onerror = () => reject(new Error('No se pudo cargar el HTML de la receta.'));
    iframe.srcdoc = fullHtmlString;
  });
}

async function waitForAssetsToRender(iframeWindow: Window): Promise<void> {
  try {
    await (iframeWindow.document as Document & { fonts?: { ready: Promise<unknown> } }).fonts?.ready;
  } catch {
    // Ignora problemas de fonts y continua con una espera corta.
  }

  await new Promise(resolve => iframeWindow.setTimeout(resolve, 350));
}

function findBestPrescriptionRoot(documentRef: Document): HTMLElement {
  const body = documentRef.body;
  const directChildren = Array.from(body.children)
    .filter((node): node is HTMLElement => node instanceof HTMLElement)
    .filter(isRenderableElement);

  const directBest = pickBestCandidate(directChildren, body);
  if (directBest) {
    return directBest;
  }

  const descendants = Array.from(body.querySelectorAll('*'))
    .filter((node): node is HTMLElement => node instanceof HTMLElement)
    .filter(isRenderableElement);

  return pickBestCandidate(descendants, body) ?? body;
}

function pickBestCandidate(candidates: HTMLElement[], body: HTMLElement): HTMLElement | null {
  if (!candidates.length) {
    return null;
  }

  const bodyArea = Math.max(body.scrollWidth * body.scrollHeight, 1);
  let bestCandidate: HTMLElement | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const candidate of candidates) {
    const width = Math.max(candidate.scrollWidth, candidate.clientWidth, candidate.offsetWidth);
    const height = Math.max(candidate.scrollHeight, candidate.clientHeight, candidate.offsetHeight);
    const area = width * height;

    if (area < 40000) {
      continue;
    }

    const style = window.getComputedStyle(candidate);
    const hasVisualContainer = style.backgroundColor !== 'rgba(0, 0, 0, 0)'
      || style.borderTopWidth !== '0px'
      || style.borderRightWidth !== '0px'
      || style.borderBottomWidth !== '0px'
      || style.borderLeftWidth !== '0px'
      || style.boxShadow !== 'none';
    const containsStructuredContent = !!candidate.querySelector('table, img, svg, canvas');
    const isAlmostWholeBody = area >= bodyArea * 0.94;

    let score = area;

    if (containsStructuredContent) {
      score += bodyArea * 0.25;
    }

    if (hasVisualContainer) {
      score += bodyArea * 0.15;
    }

    if (candidate.parentElement === body) {
      score += bodyArea * 0.05;
    }

    if (isAlmostWholeBody) {
      score -= bodyArea * 0.35;
    }

    if (score > bestScore) {
      bestScore = score;
      bestCandidate = candidate;
    }
  }

  return bestCandidate;
}

function isRenderableElement(element: HTMLElement): boolean {
  const tagName = element.tagName.toUpperCase();
  if (tagName === 'SCRIPT' || tagName === 'STYLE' || tagName === 'LINK' || tagName === 'META') {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  const width = Math.max(element.scrollWidth, element.clientWidth, element.offsetWidth);
  const height = Math.max(element.scrollHeight, element.clientHeight, element.offsetHeight);

  return width > 100 && height > 100;
}

function cropCanvasWhitespace(sourceCanvas: HTMLCanvasElement, padding: number = 0): HTMLCanvasElement {
  const context = sourceCanvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return sourceCanvas;
  }

  const { width, height } = sourceCanvas;
  const { data } = context.getImageData(0, 0, width, height);

  let top = height;
  let left = width;
  let right = -1;
  let bottom = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4;
      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      const alpha = data[index + 3];

      const isVisiblePixel = alpha > 16 && (red < 250 || green < 250 || blue < 250);
      if (!isVisiblePixel) {
        continue;
      }

      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }

  if (right < left || bottom < top) {
    return sourceCanvas;
  }

  left = Math.max(0, left - padding);
  top = Math.max(0, top - padding);
  right = Math.min(width - 1, right + padding);
  bottom = Math.min(height - 1, bottom + padding);

  const croppedWidth = right - left + 1;
  const croppedHeight = bottom - top + 1;
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = croppedWidth;
  croppedCanvas.height = croppedHeight;

  const croppedContext = croppedCanvas.getContext('2d');
  if (!croppedContext) {
    return sourceCanvas;
  }

  croppedContext.fillStyle = '#ffffff';
  croppedContext.fillRect(0, 0, croppedWidth, croppedHeight);
  croppedContext.drawImage(
    sourceCanvas,
    left,
    top,
    croppedWidth,
    croppedHeight,
    0,
    0,
    croppedWidth,
    croppedHeight
  );

  return croppedCanvas;
}

function normalizePdfFileName(fileName: string): string {
  const sanitized = (fileName || 'receta.pdf')
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
    .trim();

  return sanitized.toLowerCase().endsWith('.pdf') ? sanitized : `${sanitized}.pdf`;
}

function cmToPx(cm: number): number {
  const safeCm = Number.isFinite(cm) ? Math.max(cm, 0) : 0;
  return Math.round((safeCm / 2.54) * 96);
}
